// Components
import MotionLink from '../../../components/motionLink/motionLink';
import Container from '../../../components/containers/container/container';
import ContentContainer from '../../../components/containers/contentContainer/contentContainer';

// Utils
import { fadeIntoView } from '../../../utils/animations';
import { pageNotFoundSVG } from './utils/pageNotFoundSVG';

// Styles
import '../altPages.css';

/**
 * `PageNotFound` Component
 *
 * @remarks
 * The `PageNotFound` component renders a 404 "Page Not Found" view with
 * enhanced visuals and animations to improve user experience when navigating
 * to a nonexistent route.
 *
 * Key Features:
 * 1. **Engaging Visuals**: Uses animated SVG and gradient text effects.
 * 2. **Intuitive Navigation**: Provides a link to guide users to the home page.
 * 4. **Animations**: Uses `framer-motion` for engaging entrance animations.
 *
 * ## Structure:
 * - **Icon**: The `mainSvg` representing a visual element for 404.
 * - **Content Block**: Contains titles and descriptions about the missing page.
 * - **Navigation Link**: A link guiding users back to the home page.
 *
 * @returns
 * JSX Element for the 404 Page Not Found user interface.
 *
 * @dependencies
 * - `motion`: From `framer-motion` library, used for animations.
 * - `fadeIntoView`: Animates elements with a fade-in effect.
 * - `Container`, `ContentContainer`: Components used for layout/styling.
 * - `MotionLink`: Motion-enhanced link component for navigation.
 *
 * @example
 * ```tsx
 * // Used in route config to handle unmatched routes.
 * <Route path="*" component={PageNotFound} />
 * ```
 *
 * ## Usage Notes:
 * - Ensure required dependencies (e.g, components) are imported.
 * - `PageNotFound` is used as a fallback in route configurations.
 * - Adjust SVG graphics, content blocks, or CSS classes to modify appearance.
 */
export default function PageNotFound() {
  return (
    <Container
      semanticType="main"
      noContentContainer
      padded={false}
      id="pageNotFound"
      className="altPage"
    >
      {pageNotFoundSVG}
      <ContentContainer
        {...fadeIntoView({ fadeDirection: 'bottom' })}
        className="altPageContentContainer"
      >
        <ContentContainer className="altPageContentBlock">
          <h2 className="textGradient altPageH2">404 Page Not Found</h2>
          <h4>
            It seems that the page you were trying to reach does not exist
            anymore, or maybe it has just moved.
          </h4>
        </ContentContainer>

        <MotionLink className="altPageLink" to="/" replace>
          Go to home page
        </MotionLink>
      </ContentContainer>
    </Container>
  );
}
